import React from 'react';
import { BsEnvelope, BsWhatsapp, BsPencilSquare } from 'react-icons/bs';
import { apiFetch, parseJsonSafe, handleUnauthorized } from '../../utils/api';

// Estados de un ajuste de desarrollo, en el orden en que suelen pasar.
export const STATUS = {
  pendiente: { label: 'Pendiente de aprobar', badge: 'primary', hint: 'Revisa el ajuste y envíalo a Claude.' },
  en_cola: { label: 'En cola del servidor', badge: 'info text-dark', hint: 'El servidor lo recogerá en unos minutos.' },
  en_curso: { label: 'Claude trabajando', badge: 'info text-dark', hint: 'Claude Code lo está aplicando en el servidor.' },
  duda: { label: 'Duda para el cliente', badge: 'warning text-dark', hint: 'Claude necesita un dato del cliente. Revisa la pregunta y envíasela.' },
  esperando_cliente: { label: 'Esperando al cliente', badge: 'secondary', hint: 'Te avisaremos cuando el cliente responda (con el envío directo activado, su respuesta vuelve sola a Claude).' },
  respondido: { label: 'El cliente respondió', badge: 'warning text-dark', hint: 'Lee la respuesta del cliente y, si está bien, envíasela a Claude.' },
  en_staging: { label: 'Listo en staging', badge: 'success', hint: 'Revísalo en staging y publícalo cuando esté bien.' },
  publicando: { label: 'Publicando', badge: 'info text-dark', hint: 'El servidor lo está pasando a producción.' },
  publicado: { label: 'Publicado', badge: 'dark', hint: 'Ya está en producción.' },
  manual: { label: 'Para hacer a mano', badge: 'warning text-dark', hint: 'El proyecto no está conectado a un servidor.' },
  hecho: { label: 'Hecho a mano', badge: 'dark', hint: 'Terminado.' },
  rechazado: { label: 'Descartado', badge: 'light text-dark border', hint: 'No se ha aplicado.' },
  error: { label: 'Error', badge: 'danger', hint: 'Algo falló en el servidor. Revisa el detalle y reinténtalo.' }
};

export const FILTERS = [
  { key: 'atencion', label: 'Te esperan', params: { attention: 'true' } },
  { key: 'marcha', label: 'En marcha', params: { status: 'en_cola,en_curso,esperando_cliente,publicando' } },
  { key: 'terminados', label: 'Terminados', params: { status: 'publicado,hecho,rechazado' } },
  { key: 'todos', label: 'Todos', params: {} }
];

export const PRIORITY = { alta: 'Alta', media: 'Media', baja: 'Baja' };

export const StatusPill = ({ status }) => {
  const s = STATUS[status] || { label: status || '-', badge: 'secondary' };
  return <span className={`badge bg-${s.badge}`}>{s.label}</span>;
};

export const SourceIcon = ({ source }) => {
  if (source === 'email') return <BsEnvelope title="Llegó por email" className="text-muted" />;
  if (source === 'whatsapp') return <BsWhatsapp title="Llegó por WhatsApp" className="text-success" />;
  return <BsPencilSquare title="Creado a mano" className="text-muted" />;
};

const authHeaders = (extra = {}) => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, ...extra });

// Llamada JSON a la API de ajustes; lanza un Error con el mensaje del servidor.
export async function devApi(path, { method = 'GET', body } = {}) {
  const res = await apiFetch(`/api/dev-requests${path}`, {
    method,
    headers: authHeaders(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    body: body !== undefined ? JSON.stringify(body) : undefined
  });
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error('Sesión caducada');
  }
  const data = await parseJsonSafe(res);
  if (!res.ok) throw new Error(data.message || 'Error en la petición');
  return data;
}

export async function devUpload(path, file) {
  const form = new FormData();
  form.append('file', file);
  const res = await apiFetch(`/api/dev-requests${path}`, { method: 'POST', headers: authHeaders(), body: form });
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error('Sesión caducada');
  }
  const data = await parseJsonSafe(res);
  if (!res.ok) throw new Error(data.message || 'No se pudo subir el archivo');
  return data;
}

// Tipos que se pueden previsualizar sin riesgo: no ejecutan código (un SVG o un HTML que
// mande el cliente sí podría, y abierto aquí tendría acceso a la sesión).
const PREVIEW_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf', 'text/plain', 'text/csv'];

// Abre o descarga un adjunto (la descarga necesita el token, así que pasa por un blob).
export async function openDevFile(fileId, filename) {
  const res = await apiFetch(`/api/dev-requests/files/${fileId}/download`, { headers: authHeaders() });
  if (!res.ok) {
    const data = await parseJsonSafe(res);
    throw new Error(data.message || 'No se pudo abrir el archivo');
  }
  const type = String(res.headers.get('Content-Type') || '').split(';')[0].trim().toLowerCase();
  const buffer = await res.arrayBuffer();
  const preview = PREVIEW_TYPES.includes(type);
  const url = URL.createObjectURL(new Blob([buffer], { type: preview ? type : 'application/octet-stream' }));
  if (preview) {
    window.open(url, '_blank', 'noopener');
  } else {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'adjunto';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export const formatSize = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};
